import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { getTransferTarget } from "../../service/notes.service";
import type { TransferTarget } from "../../types/index.type";
import { eventEmitter } from "../../utils/eventEmitter";
import {
  EMPTY_ENTRY,
  TARGET_BACKGROUND_CAP_MS,
  TARGET_FETCH_RETRY_DELAYS_MS,
  TARGET_SETTLE_CAP_MS,
  type TargetEntry,
  targetPollDelay,
} from "./transferTarget";

/**
 * The one-click Transfer target per note (GET /rpa/agent/transfer/target/),
 * held by the TransferProvider so every button showing a note shares one
 * entry and one polling chain.
 */
export const useTransferTargets = (ehrTransferEnabled: boolean) => {
  // One-click targets, keyed by audio id. Mirrored in a ref so the (stable)
  // fetcher can read the current entry without re-creating on every change.
  const [targets, setTargets] = useState<Record<number, TargetEntry>>({});
  const targetsRef = useRef<Record<number, TargetEntry>>({});
  const targetTimersRef = useRef<Record<number, NodeJS.Timeout>>({});
  // Bumped whenever a note's polling chain restarts; a response or timer from
  // an older chain sees a different number and stops (no duplicate chains).
  const targetGenRef = useRef<Record<number, number>>({});
  const nextGenRef = useRef(0);
  // How many mounted buttons show each note.
  const targetMountsRef = useRef<Record<number, number>>({});
  const providerAliveRef = useRef(true);

  const setTarget = useCallback((audioId: number, patch: Partial<TargetEntry>) => {
    const next = { ...(targetsRef.current[audioId] ?? EMPTY_ENTRY), ...patch };
    targetsRef.current = { ...targetsRef.current, [audioId]: next };
    setTargets(targetsRef.current);
  }, []);

  // Read the one-click target for a note. The primary click never waits on
  // this — every state short of "ready" takes today's path. The read is a pure
  // cache read on the backend; it never starts an EHR login.
  //  - "loading", or "ready" while a re-read is in flight (`refreshing`):
  //    re-ask on targetPollDelay (fast, then slower). Past
  //    TARGET_SETTLE_CAP_MS a loading entry is marked `slow` (button reads as
  //    today's) and polling carries on quietly until TARGET_BACKGROUND_CAP_MS;
  //    then it settles ("unavailable" with "Try again" if still loading).
  //  - A failed request (network, 5xx) is retried on
  //    TARGET_FETCH_RETRY_DELAYS_MS; after that the last good block is kept,
  //    else today's button. A 404 (backend without the endpoint) is final.
  //  - One chain per note: starting a new one invalidates the old (generation
  //    number), and nothing runs after the provider unmounts.
  //  - Polling pauses while the tab is hidden; the visibility handler below
  //    re-reads on return.
  const fetchTarget = useCallback(
    async (audioId: number, force: boolean, ehrRefresh = false) => {
      const current = targetsRef.current[audioId] ?? EMPTY_ENTRY;
      if (current.fetching && !force) return;
      if (targetTimersRef.current[audioId]) {
        clearTimeout(targetTimersRef.current[audioId]);
        delete targetTimersRef.current[audioId];
      }
      const held = targetGenRef.current[audioId];
      const gen = force || held == null ? ++nextGenRef.current : held;
      targetGenRef.current[audioId] = gen;
      const stale = () => !providerAliveRef.current || targetGenRef.current[audioId] !== gen;
      const firstRequestedAt = force || current.firstRequestedAt == null ? Date.now() : current.firstRequestedAt;
      const errorCount = force ? 0 : current.errorCount;
      setTarget(audioId, { fetching: true, firstRequestedAt, errorCount, ...(force ? { slow: false } : {}) });
      const schedule = (delayMs: number) => {
        if (targetTimersRef.current[audioId]) clearTimeout(targetTimersRef.current[audioId]);
        targetTimersRef.current[audioId] = setTimeout(() => {
          delete targetTimersRef.current[audioId];
          if (stale()) return;
          // Hidden tab: stop here; the visibility handler re-reads on return.
          if (document.visibilityState === "hidden") return;
          void fetchTarget(audioId, false);
        }, delayMs);
      };
      try {
        const res = await getTransferTarget(audioId, ehrRefresh);
        if (stale()) return;
        const target = (res.data ?? null) as TransferTarget | null;
        const stillLoading = !!target?.enabled && target.state === "loading";
        const refreshing = !!target?.enabled && !!target.refreshing;
        const elapsed = Date.now() - firstRequestedAt;
        if ((stillLoading || refreshing) && elapsed < TARGET_BACKGROUND_CAP_MS) {
          const slow = stillLoading && elapsed >= TARGET_SETTLE_CAP_MS;
          setTarget(audioId, { target, fetching: false, settled: !stillLoading || slow, slow, errorCount: 0 });
          schedule(targetPollDelay(elapsed));
          return;
        }
        setTarget(audioId, {
          // A schedule still loading after the backend's retries have had
          // their time reads as unavailable: the caret offers "Try again".
          target: stillLoading && target ? { ...target, state: "unavailable", reason: "settle_timeout" } : target,
          fetching: false,
          settled: true,
          slow: false,
          errorCount: 0,
        });
      } catch (error) {
        if (stale()) return;
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status === 401) {
          setTarget(audioId, { fetching: false });
          return;
        }
        const retryDelay = TARGET_FETCH_RETRY_DELAYS_MS[errorCount];
        if (status !== 404 && retryDelay != null) {
          setTarget(audioId, { fetching: false, errorCount: errorCount + 1 });
          schedule(retryDelay);
          return;
        }
        // Out of retries: keep a previously good, settled block; a block that
        // was still loading becomes "unavailable" (never a frozen shimmer).
        // No endpoint (404): today's button.
        const held = status === 404 ? null : current.target;
        const keep = !held?.enabled
          ? null
          : held.state === "loading"
            ? { ...held, state: "unavailable" as const, reason: "settle_timeout" }
            : held;
        setTarget(audioId, { target: keep, fetching: false, settled: true, slow: false, errorCount: 0 });
      }
    },
    [setTarget],
  );

  const ensureTarget = useCallback(
    (audioId: number) => {
      if (!audioId || !ehrTransferEnabled) return;
      targetMountsRef.current[audioId] = (targetMountsRef.current[audioId] ?? 0) + 1;
      const current = targetsRef.current[audioId];
      // Settled, or a live chain already running: nothing to do. An entry left
      // unsettled by an earlier mount (its chain stopped on release) restarts.
      if (current?.settled || current?.fetching || targetTimersRef.current[audioId]) return;
      void fetchTarget(audioId, current?.firstRequestedAt != null);
    },
    [fetchTarget, ehrTransferEnabled],
  );

  const releaseTarget = useCallback((audioId: number) => {
    const left = (targetMountsRef.current[audioId] ?? 0) - 1;
    if (left > 0) {
      targetMountsRef.current[audioId] = left;
      return;
    }
    delete targetMountsRef.current[audioId];
    // Nobody shows this note any more: stop its chain (an in-flight response
    // is dropped as stale) and drop the entry, so a later mount re-reads the
    // cheap cache-only target instead of trusting a snapshot of unknown age.
    // Deleting the generation stales any response still in flight (its number
    // no longer matches), and a later chain takes a fresh, unique one.
    delete targetGenRef.current[audioId];
    if (targetTimersRef.current[audioId]) {
      clearTimeout(targetTimersRef.current[audioId]);
      delete targetTimersRef.current[audioId];
    }
    const { [audioId]: _dropped, ...rest } = targetsRef.current;
    targetsRef.current = rest;
    setTargets(rest);
  }, []);

  const refreshTarget = useCallback(
    (audioId: number, ehrRefresh = false) => {
      if (!audioId || !ehrTransferEnabled) return;
      void fetchTarget(audioId, true, ehrRefresh);
    },
    [fetchTarget, ehrTransferEnabled],
  );
  const refreshTargetRef = useRef(refreshTarget);
  useEffect(() => {
    refreshTargetRef.current = refreshTarget;
  }, [refreshTarget]);

  // Re-read the notes on screen (never every note opened this session).
  const refreshMountedTargets = useCallback(() => {
    for (const key of Object.keys(targetMountsRef.current)) {
      const audioId = Number(key);
      refreshTargetRef.current(audioId);
    }
  }, []);

  // Tab return: the schedule may have moved while the clinician was in the
  // EHR, and polling paused while hidden.
  useEffect(() => {
    if (!ehrTransferEnabled) return;
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshMountedTargets();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [ehrTransferEnabled, refreshMountedTargets]);

  // Provider unmount (logout, leaving the authenticated layout): stop every
  // chain, including a request still in flight.
  useEffect(() => {
    providerAliveRef.current = true;
    return () => {
      providerAliveRef.current = false;
      for (const timer of Object.values(targetTimersRef.current)) clearTimeout(timer);
      targetTimersRef.current = {};
    };
  }, []);

  // A completed transfer tags its encounter "Note transferred" for other notes
  // of the same patient, so the notes on screen are re-read.
  useEffect(() => {
    eventEmitter.on("ehrTransferCompleted", refreshMountedTargets);
    return () => {
      eventEmitter.off("ehrTransferCompleted", refreshMountedTargets);
    };
  }, [refreshMountedTargets]);

  const getTargetForAudio = useCallback((audioId: number): TargetEntry => targets[audioId] ?? EMPTY_ENTRY, [targets]);

  return { getTargetForAudio, ensureTarget, releaseTarget, refreshTarget, refreshTargetRef };
};
